import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { toBase64, getFileExtension } from '../../../utils/util';

import {
  requestCreateReaction,
  requestDeletePost,
  requestViewComments,
  requestRetrieveChat,
  requestUpdateComment,
} from '../../../api/market-buzz/market-buzz.actions';

import Comment from '../../../components/common/comment/Comment';

const CommentContainer = ({
  postExternalReference,
  parentExternalReference,
  comment,
  reactions,
  adminRole,
  indent,
  showAllComments,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchingComments = useRef(false);

  useEffect(() => {
    if (!comment.externalReference || fetchingComments.current || !postExternalReference || !showAllComments) return;

    fetchingComments.current = true;
    dispatch(
      requestViewComments({
        postExternalReference: comment.externalReference,
        page: 0,
        size: 99,
        parentPostExternalReference: postExternalReference,
        onSuccess: () => (fetchingComments.current = false),
      })
    );
  }, [dispatch, comment.externalReference, postExternalReference, showAllComments]);

  const createReaction = (reactionType, onError) => {
    const reaction = {
      reaction: reactionType,
      parentPostExternalReference: comment.externalReference,
    };

    dispatch(requestCreateReaction({ reaction, onError }));
  };

  const deleteReaction = (reaction, onError) => {
    dispatch(requestDeletePost({ postExternalReference: reaction.externalReference, reaction: true, onError }));
  };

  const deleteComment = (commentExternalReference) => {
    dispatch(
      requestDeletePost({
        postExternalReference: commentExternalReference,
        parentPostExternalReference: postExternalReference,
        parentCommentExternalReference: parentExternalReference,
      })
    );
  };

  const retrieveChat = (postCreatorExternalReference) => {
    dispatch(
      requestRetrieveChat({
        postCreatorExternalReference,
        onSuccess: (chatExternalReference) => navigate(`/market/direct-messages/${chatExternalReference}`),
      })
    );
  };

  const mapImages = async (files) => {
    const mappedFiles = [];
    const linkedMediaExternalReferences = [];

    for (const file of files) {
      if (typeof file === 'string') {
        linkedMediaExternalReferences.push(file);
        continue;
      }

      mappedFiles.push({
        base64String: await toBase64(file),
        documentExtension: getFileExtension(file.name),
      });
    }

    return [mappedFiles, linkedMediaExternalReferences];
  };

  const updateComment = async (text, images, onSuccess) => {
    const [photos, linkedMediaExternalReferences] = await mapImages(images);
    const commentToUpdate = {
      postExternalReference: comment.externalReference,
      photos,
      linkedMediaExternalReferences,
      text,
    };

    dispatch(
      requestUpdateComment({
        comment: commentToUpdate,
        postExternalReference,
        parentExternalReference,
        onSuccess,
      })
    );
  };

  return (
    <Comment
      postExternalReference={postExternalReference}
      comment={comment}
      reactions={reactions}
      adminRole={adminRole}
      indent={indent}
      showAllComments={showAllComments}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      retrieveChat={retrieveChat}
      updateComment={updateComment}
      deleteComment={deleteComment}
    />
  );
};

CommentContainer.propTypes = {
  postExternalReference: PropTypes.string.isRequired,
  parentExternalReference: PropTypes.string.isRequired,
  comment: PropTypes.shape({}).isRequired,
  reactions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  adminRole: PropTypes.bool,
  indent: PropTypes.number.isRequired,
  showAllComments: PropTypes.bool.isRequired,
};

CommentContainer.defaultProps = {
  adminRole: false,
};

export default CommentContainer;
